import { LoadingSpinner } from "components/LoadingSpinner";
import { orderTypeConstants, orderStatusConstants } from "constants/order";
import { events } from "constants/tagManager";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import dayjs from "dayjs";
import { parseDate } from "helpers/date";
import { resolveOrderBenefits } from "helpers/orderBenefits";
import { centsToDollars, parsePrice } from "helpers/price";
import { useAnalytics } from "hooks/useAnalytics";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useFinixPaymentProvider } from "hooks/useFinixPaymentProvider";
import { useQuery } from "hooks/useQuery";
import { debounce, isEmpty } from "lodash";
import { groupByCodeDescriptionAndCount } from "pages/OrderOverview/components/Overview/helpers";
import { PaymentSuccessFull } from "pages/OrderOverview/components/PaymentSection/PaymentSuccessFull/PaymentSuccessFull";
import { validateNextInstallmentDate } from "pages/OrderView/components/PaymentDetails/helpers/helper";
import { normalizePaymentPlanBreakdown } from "pages/OrderView/components/ProviderPayoutCalculations/helpers";
import { Receipt } from "pages/VerificationPage/components/Receipt/Receipt";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { useEffect, useMemo, useState, useContext } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  useGetPublicOrderQuery,
  useUpdateNextInstallmentDateMutation,
} from "store/queries/order";
import { IOrder } from "types/Order/Order";
import { IPaymentPlan } from "types/Payment";
import { IProvider, SpecialtyListDict } from "types/Provider/Provider";

import { ServiceInfo } from "../../../OrderOverview/components/Overview/components/ServiceInfo";
import BalanceHistory from "./components/BalanceHistory";
import BalanceLine from "./components/BalanceLine";
import EditCardDetails from "./components/EditCardDetails";
import FinixEditCardDetails from "./components/FinixEditCardDetails";
import InfoRow from "./components/InfoRow";
import PayMoreSection from "./components/PayMoreSection";
import Questions from "./components/Questions";
import { UpdateSuccessFull } from "./components/UpdateSuccessfull";
import {
  StyledBreadcrumbContainer,
  StyledBreadCrumb,
  StyledPageContainer,
  StyledProgressBar,
  StyledCardContainer,
  StyledButton,
} from "./styled";

export function Update() {
  useDocumentTitle("Order Credit Card Update");
  const { id: externalId } = useParams();
  const query = useQuery();
  const [updateNextInstallmentDate] = useUpdateNextInstallmentDateMutation();
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [showPaymentsSection, setShowPaymentsSection] = useState(false);
  const [showUpdateSuccessFull, setShowUpdateSuccessFull] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showPaymentSection, setShowPaymentSection] = useState({
    paymentPlan: false,
    paymentSection: false,
    paymentSuccess: false,
    receiptAvailable: false,
  });
  const [loading, setLoading] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [nextInstallmentDate, setNextInstallmentDate] = useState<
    string | null | Date
  >(null);
  const [minDate, setMinDate] = useState<Date>();
  const [maxDate, setMaxDate] = useState<Date>();
  const { userInfo } = useUserWithAccount();
  const { current: toastElement } = useContext(ToastContext);

  const internalUser = query.get("internalUser");

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { pageViewEvents } = useAnalytics();

  const { data, isLoading, isError, refetch, isFetching } =
    useGetPublicOrderQuery(externalId || "");

  const orderInfo = useMemo(() => {
    return data?.data as IOrder;
  }, [data]);

  // CLE-2635 — orders with no quote still carry eligibility in benefitDetails.
  const benefits = useMemo(() => resolveOrderBenefits(orderInfo), [orderInfo]);

  const nextInstallmentInfo = useMemo(() => {
    if (!orderInfo) return null;
    const activePaymentPlan = orderInfo.paymentPlan.find(
      (paymentPlan) => paymentPlan.active && paymentPlan?.installments?.length
    ) as IPaymentPlan;
    if (!activePaymentPlan) return null;
    const { installments } = activePaymentPlan;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sortedInstallments = [...(installments || [])]
      .filter((installment) => {
        const installmentDate = new Date(installment.dateDue);
        installmentDate.setHours(0, 0, 0, 0); // Reset installment's time to midnight
        return installmentDate >= today; // Compare only the date parts
      })
      .sort(
        (a, b) => new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime()
      ); // Sort by dateDue
    // first installment with a null paymentId
    const firstNullPaymentIdInstallment = sortedInstallments.find(
      (installment) => !installment.paymentId
    );
    const nextInstallmentDate = firstNullPaymentIdInstallment?.dateDue
      ? parseDate(firstNullPaymentIdInstallment.dateDue, false, true)
      : null;
    return {
      date: nextInstallmentDate,
      amount: firstNullPaymentIdInstallment?.amountInCents,
    };
  }, [orderInfo]);

  useEffect(() => {
    if (nextInstallmentInfo?.date) {
      const baseDate = dayjs(nextInstallmentInfo.date, "MM/DD/YYYY");
      const dateObject = baseDate.toDate();
      const fifteenDaysBefore = baseDate.subtract(15, "day");
      const tomorrow = dayjs().add(1, "day").startOf("day");
      const effectiveMinDate = fifteenDaysBefore.isAfter(tomorrow)
        ? fifteenDaysBefore
        : tomorrow;
      const min = effectiveMinDate.toDate();
      const max = baseDate.add(15, "day").toDate();
      setNextInstallmentDate(dateObject as Date);
      setMinDate(min as Date);
      setMaxDate(max as Date);
    } else {
      setNextInstallmentDate(null);
    }
  }, [nextInstallmentInfo]);

  const editButtonVisible = useMemo(() => {
    if (!orderInfo) return false;
    // Account-level opt-out. The public order returns the raw features column, so the flag is
    // absent for every account that has never been saved since it shipped — hence `=== false`
    // rather than a truthiness check, which would hide the button for all of them.
    if (
      orderInfo.account?.features?.changeNextPaymentDueDateEnabled === false
    ) {
      return false;
    }
    const { orderHistories } = orderInfo;
    const patientAlreadyChangedNextInstallmentDate = orderHistories.some(
      (history) =>
        history.type === "NextInstallmentDateChanged" && !history.userId
    );
    return !patientAlreadyChangedNextInstallmentDate;
  }, [orderInfo]);

  const [editing, setEditing] = useState(false);

  const nextInstallmentValidationMessage = useMemo(() => {
    if (!editing || !nextInstallmentDate) return null;
    const dateStr = parseDate(nextInstallmentDate, false, true);
    const result = validateNextInstallmentDate(dateStr, "future");
    return result === true ? null : result;
  }, [editing, nextInstallmentDate]);

  const totalPaid = useMemo(() => {
    if (!orderInfo) return null;
    const paidInCents = orderInfo.payments.reduce((total, payment) => {
      return total + (payment.amountInCents || 0);
    }, 0);
    return centsToDollars(paidInCents);
  }, [orderInfo]);

  const remainingInstallmentsInfo = useMemo(() => {
    if (!orderInfo) return null;
    const activePaymentPlan = orderInfo.paymentPlan.find(
      (paymentPlan) => paymentPlan.active && paymentPlan?.installments?.length
    ) as IPaymentPlan;
    if (!activePaymentPlan) return null;
    const { installments } = activePaymentPlan;
    const remainingInstallments = installments?.filter(
      (installment) => !installment.paymentId
    );
    const remainingPayments = remainingInstallments?.length;
    const installmentAmount = remainingInstallments?.[0]?.amountInCents;
    return { remainingPayments, installmentAmount };
  }, [orderInfo]);

  const estimatedCost = useMemo(() => {
    if (!orderInfo) return null;
    const activePaymentPlan = orderInfo.paymentPlan.find(
      (paymentPlan) => paymentPlan.active
    ) as IPaymentPlan;
    const orderHadPaymentPlan =
      orderInfo.paymentPlanBreakdown?.payments?.length;
    if (!orderHadPaymentPlan) return null;
    const remainingAmount = centsToDollars(
      activePaymentPlan?.remainingAmountInCents || 0
    );
    return (totalPaid || 0) + (remainingAmount || 0);
  }, [orderInfo, totalPaid]);

  const remainingAmount = useMemo(() => {
    if (!orderInfo || estimatedCost === null) return null;
    return estimatedCost - (totalPaid || 0);
  }, [estimatedCost, totalPaid]);

  const progressBarValue = useMemo(() => {
    if (!orderInfo || estimatedCost === null) return null;
    return Math.round(((totalPaid || 0) / estimatedCost) * 100);
  }, [estimatedCost, totalPaid]);

  const isOrderPaid = useMemo(() => {
    if (!orderInfo) return false;
    return orderInfo.status === orderStatusConstants.PAID;
  }, [orderInfo]);

  const addClearFee = useMemo(() => {
    if (!orderInfo) return false;
    const { orderType } = orderInfo;
    return (
      orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY &&
      orderType !== orderTypeConstants.GFE
    );
  }, [orderInfo]);

  const currentProvidersPaidInfo = useMemo(() => {
    if (!orderInfo) return null;
    const payments = orderInfo?.paymentPlanBreakdown?.payments
      ? normalizePaymentPlanBreakdown(orderInfo.paymentPlanBreakdown.payments)
      : undefined;
    if (!payments || !payments.length) return null;

    // Get payments that should be included in the calculation
    const paidPayments = payments.filter((payment, index) => {
      // First payment is always included
      if (index === 0) return true;
      // Other payments only if they have been paid (paymentId exists)
      return payment.installmentInDb?.paymentId !== null;
    });

    // Calculate total paid amount per provider
    const providerPaidAmounts = new Map<
      string,
      {
        providerId: string;
        providerName: string;
        alreadyPaidAmount: number;
        specialty: string;
        clearDiscountedPrice: number;
        netTransfers: number;
      }
    >();

    const { clearFeeValueInPercentage } = orderInfo;

    paidPayments.forEach((payment) => {
      payment.providerBreakdown.forEach((providerPayment: any) => {
        const { providerId } = providerPayment;
        const existing = providerPaidAmounts.get(providerId);

        const paymentAmount = addClearFee
          ? (providerPayment.clearDiscountedPrice || 0) *
            (1 + Number(clearFeeValueInPercentage) / 100)
          : providerPayment.clearDiscountedPrice || 0;
        const lastPaymentAmount = addClearFee
          ? (providerPayment.clearDiscountedPrice || 0) *
            (1 + Number(clearFeeValueInPercentage) / 100)
          : providerPayment.clearDiscountedPrice;

        if (existing) {
          existing.alreadyPaidAmount += paymentAmount;
          existing.clearDiscountedPrice = lastPaymentAmount;
        } else {
          providerPaidAmounts.set(providerId, {
            providerId,
            providerName: providerPayment.providerName,
            alreadyPaidAmount: paymentAmount,
            specialty: providerPayment.specialty,
            clearDiscountedPrice: lastPaymentAmount,
            netTransfers: 0,
          });
        }
      });
    });

    // Calculate full amounts for each provider by summing across ALL payments
    const providerFullAmounts = new Map<string, number>();

    payments.forEach((payment) => {
      payment.providerBreakdown.forEach((providerPayment: any) => {
        const { providerId } = providerPayment;
        const paymentAmount = providerPayment.clearDiscountedPrice || 0;
        const existing = providerFullAmounts.get(providerId) || 0;
        providerFullAmounts.set(providerId, existing + paymentAmount);
      });
    });

    // Create provider breakdown with cumulative paid amounts and full amounts
    const providerBreakdown = Array.from(providerPaidAmounts.values()).map(
      (provider) => {
        const fullAmount = providerFullAmounts.get(provider.providerId) || 0;
        return {
          ...provider,
          alreadyPaidAmount: provider.alreadyPaidAmount, // Total paid so far
          clearDiscountedPrice: provider.clearDiscountedPrice,
          clearDiscountedPriceFull: addClearFee
            ? fullAmount * (1 + Number(clearFeeValueInPercentage) / 100)
            : fullAmount,
        };
      }
    );

    // Find the last paid payment for the date
    const lastPaidPayment = [...paidPayments]
      .reverse()
      .find((p) => p.installmentInDb?.paymentId !== null);
    const dateDue = lastPaidPayment?.dateDue || paidPayments[0]?.dateDue;
    const transfers = orderInfo.orderHistories.filter(
      (history) => history.type === "Transfer"
    );
    const calculateNetTransfers = (providerId: string) => {
      let netTransfers = 0;
      transfers.forEach((transfer) => {
        if (transfer.toProviderId === providerId) {
          // Money received
          netTransfers += transfer.amountInCents || 0;
        }
        if (transfer.fromProviderId === providerId) {
          // Money sent
          netTransfers -= transfer.amountInCents || 0;
        }
      });
      return netTransfers;
    };
    providerBreakdown.forEach((provider) => {
      provider.netTransfers = calculateNetTransfers(provider.providerId);
    });

    return {
      providerBreakdown,
      dateDue,
    };
  }, [orderInfo, addClearFee]);

  const payMoreButtonDisabled = useMemo(() => {
    if (!orderInfo) {
      return false;
    }
    const payments = orderInfo?.paymentPlanBreakdown?.payments;
    if (!payments || !payments.length) return false;
    if (
      (orderInfo.status === orderStatusConstants.PARTIALLY_PAID ||
        orderInfo.status === orderStatusConstants.PAID) &&
      orderInfo?.paymentPlanBreakdown?.payments?.length
    ) {
      const iniitalDeposit = orderInfo.paymentPlanBreakdown.payments[0];
      const paymentsWithoutInitialDeposit =
        orderInfo.paymentPlanBreakdown.payments.filter(
          (p: any) => !!p.installmentInDb
        );
      const lastPaidInstallment = paymentsWithoutInitialDeposit.reduce(
        (last: any, p: any) => (p.installmentInDb?.paymentId ? p : last),
        null
      );
      if (lastPaidInstallment) {
        return !lastPaidInstallment.transferredToProviders;
      }
      return !iniitalDeposit.transferredToProviders;
    }
    return false;
  }, [orderInfo]);

  const balanceHistory = useMemo(() => {
    if (!orderInfo) return null;
    const { orderHistories } = orderInfo;
    if (!orderHistories || orderHistories.length === 0) return [];

    // Get all BalanceUpdated entries, sorted by creation date (newest first)
    const sortedBalanceUpdated = orderHistories
      .filter((h) => h.type === "BalanceUpdated")
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

    // Get all unique providers from the current order
    const allProviders = new Set<string>();
    if (currentProvidersPaidInfo?.providerBreakdown) {
      currentProvidersPaidInfo.providerBreakdown.forEach((provider: any) => {
        allProviders.add(provider.providerId);
      });
    }

    // For each provider, find the latest BalanceUpdated that affects that provider
    const latestUpdatesPerProvider = new Map<string, any>();

    allProviders.forEach((providerId) => {
      const latestUpdateForProvider = sortedBalanceUpdated.find((entry) => {
        // Check if this entry's providerValueChanges includes this provider
        return entry.providerValueChanges?.some(
          (change: any) => change.providerId === providerId
        );
      });

      if (latestUpdateForProvider) {
        latestUpdatesPerProvider.set(
          latestUpdateForProvider.id,
          latestUpdateForProvider
        );
      }
    });

    // Filter to include all BalanceReported and the latest BalanceUpdated entries for each provider
    const filtered = orderHistories.filter((h) => {
      if (h.type === "BalanceReported") return true;
      if (h.type === "BalanceUpdated" && latestUpdatesPerProvider.has(h.id))
        return true;
      return false;
    });

    // Find the most recent BalanceUpdated entry and move it to first position
    const filteredBalanceUpdated = filtered.filter(
      (h) => h.type === "BalanceUpdated"
    );

    if (filteredBalanceUpdated.length === 0) {
      return filtered;
    }

    // Find the most recent BalanceUpdated entry
    const mostRecentBalanceUpdated = filteredBalanceUpdated.reduce(
      (latest, current) => {
        return new Date(current.createdAt).getTime() >
          new Date(latest.createdAt).getTime()
          ? current
          : latest;
      }
    );

    // Return with most recent BalanceUpdated first, then all others in original order
    return [
      mostRecentBalanceUpdated,
      ...filtered.filter((h) => h.id !== mostRecentBalanceUpdated.id),
    ];
  }, [orderInfo, currentProvidersPaidInfo]);

  const procedures = useMemo(() => {
    if (!orderInfo) return [];
    return groupByCodeDescriptionAndCount(
      orderInfo.serviceDetails.procedures
    ) as any;
  }, [orderInfo]);

  const debouncedPageViewEvents = debounce((order, event: string) => {
    pageViewEvents(
      {
        accountName: order?.accountName,
        orderType: order?.orderType,
        communicationMethod: order?.communicationMethod,
      },
      event
    );
  }, 300);

  useEffect(() => {
    if (isEmpty(data)) return;

    debouncedPageViewEvents(
      {
        accountName: data?.data.account.name,
        orderType: data?.data.orderType,
        communicationMethod: data?.data.patient.preferredContactMethod,
      },
      events.CARD_DETAILS_UPDATED
    );
  }, [data]);

  const handleCancelChangingNextInstallmentDate = () => {
    const dateObject = dayjs(nextInstallmentInfo?.date, "MM/DD/YYYY").toDate();
    setNextInstallmentDate(dateObject as Date);
    setEditing(false);
  };

  useEffect(() => {
    if (!nextInstallmentDate) return;
    const initialDate = nextInstallmentInfo?.date;
    const date = parseDate(nextInstallmentDate, false, true);
    const validation = validateNextInstallmentDate(date, "future");
    const invalid = typeof validation === "string";
    if (initialDate === date || invalid) {
      setSaveDisabled(true);
    } else {
      setSaveDisabled(false);
    }
  }, [nextInstallmentDate, nextInstallmentInfo]);

  const handleSubmitChangingNextInstallmentDate = async () => {
    if (!nextInstallmentDate) return;
    // calendar automatically selects user's local date and time, so we take only the date from the local time
    const date = parseDate(nextInstallmentDate, false, true);
    const validation = validateNextInstallmentDate(date, "future");
    if (typeof validation === "string") {
      toastElement?.show({
        severity: "error",
        summary: "Invalid next payment date",
        detail: validation,
      });
      return;
    }
    try {
      setLoading(true);
      updateNextInstallmentDate({
        orderId: orderInfo.id,
        nextInstallmentDate: date,
        userId: userInfo?.id || null,
      })
        .unwrap()
        .then(() => {
          toastElement?.show({
            severity: "success",
            summary: "Next Installment Date Updated",
            detail: "Next installment date updated successfully",
          });
          refetch();
          setEditing(false);
          setLoading(false);
        })
        .catch((error) => {
          console.log("error", error);
          toastElement?.show({
            severity: "error",
            summary: "Error Updating Next Installment Date",
            detail:
              "An error occurred while updating the next installment date",
          });
          setLoading(false);
        });
    } catch (error) {
      console.log("error", error);
      toastElement?.show({
        severity: "error",
        summary: "Error Updating Next Installment Date",
        detail: "An error occurred while updating the next installment date",
      });
      setLoading(false);
    }
  };

  const publicContact = () => {
    const currentProvider = orderInfo.account
      .providers[0] as unknown as IProvider;

    const isVisible =
      currentProvider.publicContactName &&
      currentProvider.publicContactRole &&
      currentProvider.publicContactPhone;

    return {
      isVisible,
      name: currentProvider.publicContactName,
      role: currentProvider.publicContactRole,
      phone: currentProvider.publicContactPhone,
    };
  };

  const breadcrumbItems = useMemo(() => {
    const itemsBase = {
      label: "Order",
      template: (
        <Button
          onClick={() => {
            navigate(`/orders/${orderInfo.id}`);
          }}
          className="p-button-text"
        >
          Order
        </Button>
      ),
    };

    const updateItem = {
      label: "Update",
      template: <span className="active">Update</span>,
    };
    const payMoreItem = {
      label: "Pay More",
      template: <span className="active">Pay More</span>,
    };

    return [
      ...(internalUser ? [itemsBase] : []),
      {
        label: "Payment Plan Details",
        template:
          !showCardDetails &&
          !showUpdateSuccessFull &&
          !showPaymentSection.paymentSection &&
          !showPaymentSection.paymentSuccess ? (
            <span className="active">Payment Plan Details</span>
          ) : (
            <Button
              onClick={() => {
                setShowCardDetails(false);
                setShowUpdateSuccessFull(false);
                setShowPaymentSection({
                  paymentPlan: false,
                  paymentSection: false,
                  paymentSuccess: false,
                  receiptAvailable: false,
                });
                setShowReceipt(false);
              }}
              className="p-button-text"
            >
              Payment Plan Details
            </Button>
          ),
      },
      ...(!showCardDetails && !showUpdateSuccessFull ? [] : [updateItem]),
      ...(showPaymentSection.paymentSection ? [payMoreItem] : []),
    ];
  }, [
    internalUser,
    // isInternalUser,
    showCardDetails,
    showUpdateSuccessFull,
    showPaymentSection.paymentSuccess,
    showPaymentSection.paymentSection,
    orderInfo,
  ]);

  const isPatientResponsibility = useMemo(() => {
    if (!orderInfo) return false;
    return orderInfo?.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY;
  }, [orderInfo]);

  // Strict-freeze: which card-update form to show is decided by the rail this
  // order is BOUND to (authoritative endpoint via the hook), NOT the account's
  // current useFinix flag. A Stripe-era order on a flipped account must still
  // get the Stripe form.
  const { isFinix: orderIsFinix, isLoading: isProviderLoading } =
    useFinixPaymentProvider(orderInfo);

  if ((!isLoading && !data?.data) || isError) {
    return <Navigate to="/not-found" />;
  }

  if (isLoading || isFetching) {
    return <LoadingSpinner />;
  }

  if (showReceipt) {
    return (
      <div className="flex flex-column w-full align-items-center">
        <StyledButton
          icon="pi pi-arrow-left"
          onClick={() => {
            setShowReceipt(false);
            setShowCardDetails(false);
            setShowPaymentSection({
              paymentPlan: false,
              paymentSection: false,
              paymentSuccess: false,
              receiptAvailable: false,
            });
            setShowUpdateSuccessFull(false);
          }}
          className="p-button-text p-button-secondary p-0"
        >
          <p className="small-text">Return</p>
        </StyledButton>
        <Receipt order={orderInfo} showBackToOrderButton={false} />
      </div>
    );
  }

  if (showUpdateSuccessFull) {
    return (
      <UpdateSuccessFull
        publicContact={() => publicContact()}
        isInternalUser={!!internalUser}
        orderId={orderInfo.id}
        onReturnClick={() => {
          setShowUpdateSuccessFull(false);
          setShowCardDetails(false);
          setShowPaymentSection({
            paymentPlan: false,
            paymentSection: false,
            paymentSuccess: false,
            receiptAvailable: false,
          });
          setShowReceipt(false);
        }}
      />
    );
  }

  if (showPaymentSection.paymentSuccess && externalId) {
    return (
      <PaymentSuccessFull
        publicContact={publicContact}
        orderExternalId={externalId}
        receiptAvailable={showPaymentSection.receiptAvailable}
        showPaymentPlanInfo={
          remainingInstallmentsInfo?.remainingPayments !== 0 &&
          remainingInstallmentsInfo?.installmentAmount !== 0
        }
        newDuration={remainingInstallmentsInfo?.remainingPayments}
        newInstallmentAmount={remainingInstallmentsInfo?.installmentAmount}
        orderUrl={
          internalUser && userInfo ? `/orders/${orderInfo.id}` : undefined
        }
      />
    );
  }

  return (
    <div className="flex flex-column w-full align-items-center">
      <StyledBreadcrumbContainer className="p-3">
        <StyledBreadCrumb model={breadcrumbItems} />
      </StyledBreadcrumbContainer>
      <StyledPageContainer
        maxWidth={showCardDetails ? "550px" : undefined}
        minWidth={showCardDetails ? "550px" : undefined}
      >
        {showCardDetails &&
          !showPaymentSection.paymentSection &&
          isProviderLoading && <LoadingSpinner />}
        {showCardDetails &&
          !showPaymentSection.paymentSection &&
          !isProviderLoading &&
          (orderIsFinix ? (
            <FinixEditCardDetails
              orderInfo={orderInfo}
              onReturnClick={() => {
                setShowCardDetails(false);
                setShowPaymentSection({
                  paymentPlan: false,
                  paymentSection: false,
                  paymentSuccess: false,
                  receiptAvailable: false,
                });
                setShowUpdateSuccessFull(false);
                setShowReceipt(false);
              }}
              setShowUpdateSuccessFull={setShowUpdateSuccessFull}
            />
          ) : (
            <EditCardDetails
              orderInfo={orderInfo}
              onReturnClick={() => {
                setShowCardDetails(false);
                setShowPaymentSection({
                  paymentPlan: false,
                  paymentSection: false,
                  paymentSuccess: false,
                  receiptAvailable: false,
                });
                setShowUpdateSuccessFull(false);
                setShowReceipt(false);
              }}
              setShowUpdateSuccessFull={setShowUpdateSuccessFull}
            />
          ))}
        {showPaymentSection.paymentSection && (
          <PayMoreSection
            order={orderInfo}
            onReturnClick={() => {
              setShowCardDetails(false);
              setShowPaymentSection({
                paymentPlan: false,
                paymentSection: false,
                paymentSuccess: false,
                receiptAvailable: false,
              });
              setShowUpdateSuccessFull(false);
              setShowReceipt(false);
            }}
            setShowPaymentSection={setShowPaymentSection}
            onUpdateOrder={() => refetch()}
          />
        )}
        {!showCardDetails && !showPaymentSection.paymentSection && (
          <>
            {isOrderPaid && (
              <h2 className="text-base md:text-xl line-height-3 font-semibold mb-4 purple text-center">
                Payment successfully completed!
              </h2>
            )}
            <StyledCardContainer>
              <div className="flex justify-content-between align-items-center mb-2">
                <div className="flex flex-column">
                  <p className="font-semibold black text-xl md:text-2xl">
                    {parsePrice(totalPaid || 0)}
                  </p>
                  <p className="font-normal text-md md:text-md">Total Paid</p>
                </div>
                <div className="flex flex-column">
                  <p className="font-semibold black text-xl md:text-2xl">
                    {parsePrice(remainingAmount || 0)}
                  </p>
                  <p className="font-normal text-md md:text-md">Remaining</p>
                </div>
              </div>
              <StyledProgressBar
                value={progressBarValue}
                className="mb-4 mt-3"
                showValue={false}
              />
              {remainingInstallmentsInfo && (
                <>
                  <p className="font-medium text-sm mb-2 purple">
                    {remainingInstallmentsInfo.remainingPayments} payments left
                    •{" "}
                    {parsePrice(
                      centsToDollars(
                        remainingInstallmentsInfo.installmentAmount || 0
                      )
                    )}
                    /mo
                  </p>
                  <div className="flex justify-content-between w-full md:align-items-center md:flex-row flex-column gap-2">
                    <div className="flex align-items-center gap-1">
                      <p className="font-medium text-sm">
                        Next payment due:{" "}
                        {!editing && nextInstallmentInfo?.date}
                      </p>
                      {!editing && editButtonVisible && (
                        <Button
                          label="Change the date"
                          className="p-button-text p-0 w-fit text-sm ml-2"
                          style={{ color: "#4f46e5" }}
                          onClick={() => setEditing(true)}
                          disabled={isOrderPaid}
                        />
                      )}
                    </div>
                    {editing && (
                      <div className="flex flex-column gap-1 w-full sm:w-5">
                        <Calendar
                          value={nextInstallmentDate}
                          showMinMaxRange
                          minDate={minDate}
                          maxDate={maxDate}
                          onChange={(e) => {
                            setNextInstallmentDate(e.value as Date | null);
                          }}
                        />
                        <div className="flex flex-column gap-2 my-2">
                          {nextInstallmentValidationMessage && (
                            <p className="text-xs text-red-600 m-0 flex align-items-start gap-1 line-height-3">
                              <i
                                className="pi pi-times-circle mt-1 flex-shrink-0"
                                style={{ fontSize: "14px" }}
                                aria-hidden
                              />
                              <span>{nextInstallmentValidationMessage}</span>
                            </p>
                          )}
                          <p className="text-xs text-orange-500 m-0 flex align-items-start gap-1 line-height-3">
                            <i
                              className="pi pi-exclamation-triangle mt-1 flex-shrink-0"
                              style={{ fontSize: "14px" }}
                              aria-hidden
                            />
                            <span>This change can be done only once</span>
                          </p>
                        </div>

                        <div className="flex gap-2 w-full justify-content-end">
                          <Button
                            label="Save"
                            className="p-button-small p-button-sm"
                            onClick={handleSubmitChangingNextInstallmentDate}
                            loading={loading}
                            disabled={saveDisabled}
                          />
                          <Button
                            label="Cancel"
                            className="p-button-outlined p-button-sm"
                            onClick={handleCancelChangingNextInstallmentDate}
                          />
                        </div>
                      </div>
                    )}
                    <Button
                      icon="pi pi-file"
                      className="p-button-text p-button-primary p-0 mb-0 gap-2 text-sm"
                      onClick={() => {
                        setShowReceipt(true);
                        setShowCardDetails(false);
                        setShowPaymentSection({
                          paymentPlan: false,
                          paymentSection: false,
                          paymentSuccess: false,
                          receiptAvailable: false,
                        });
                        setShowUpdateSuccessFull(false);
                      }}
                    >
                      Receipt
                    </Button>
                  </div>
                </>
              )}
            </StyledCardContainer>
            <StyledCardContainer className="px-3 mt-3">
              <div className="flex flex-column w-full">
                <p className="line-height-2 font-medium text-xs">
                  SERVICES PROVIDED
                </p>
                <p className="text-base line-height-3 font-medium highlighted mt-2">
                  {parseDate(orderInfo.dateOfService)}{" "}
                </p>
                <ServiceInfo
                  title={`${procedures[0].cptDescription}`}
                  providers={
                    isPatientResponsibility
                      ? [{ name: orderInfo.account.name }]
                      : procedures[0].providers
                  }
                  quantity={procedures[0].providers[0]?.quantity}
                  code={procedures[0].code}
                />
                {showPaymentsSection &&
                  procedures
                    .slice(1)
                    .map((procedure: any) => (
                      <ServiceInfo
                        title={`${procedure.cptDescription}`}
                        providers={
                          isPatientResponsibility
                            ? [{ name: orderInfo.account.name }]
                            : procedure.providers
                        }
                        key={`${procedure.code}-${procedure.providers[0]?.id}`}
                        quantity={procedure.providers[0]?.quantity}
                        code={procedure.code}
                      />
                    ))}
              </div>
              {procedures.length > 1 && (
                <Button
                  label={
                    showPaymentsSection ? "Hide services" : "View all services"
                  }
                  className="p-button-text p-0 w-fit text-sm"
                  style={{ color: "#4f46e5" }}
                  icon={`pi pi-chevron-up transition-duration-500 transition-all ${
                    showPaymentsSection ? "" : "rotate-180"
                  }`}
                  iconPos="right"
                  onClick={() => setShowPaymentsSection(!showPaymentsSection)}
                />
              )}
            </StyledCardContainer>
            <StyledCardContainer className="mt-3">
              <p className="font-semibold text-xl black mb-3">
                Payment distribution
              </p>

              {[...(currentProvidersPaidInfo?.providerBreakdown || [])]
                .sort((a, b) => {
                  const remainingA =
                    a.clearDiscountedPriceFull - a.alreadyPaidAmount;
                  const remainingB =
                    b.clearDiscountedPriceFull - b.alreadyPaidAmount;
                  return remainingB - remainingA; // Sort descending (highest to lowest)
                })
                .map((item: any, index: number) => (
                  <BalanceLine
                    key={`${item.providerId}-${index}`}
                    title={item.providerName}
                    orderId={orderInfo.id}
                    onUpdateOrder={() => refetch()}
                    providerId={item.providerId}
                    userId={userInfo?.id ?? undefined}
                    specialty={
                      SpecialtyListDict?.[
                        item.specialty as keyof typeof SpecialtyListDict
                      ] ?? ""
                    }
                    remainingAmount={
                      item.clearDiscountedPriceFull - item.alreadyPaidAmount
                    }
                    paidAmount={item.alreadyPaidAmount + item.netTransfers}
                    fullAmount={
                      item.clearDiscountedPriceFull - item.alreadyPaidAmount <=
                      0
                        ? item.clearDiscountedPriceFull + item.netTransfers
                        : item.clearDiscountedPriceFull
                    }
                    lastPaymentDate={currentProvidersPaidInfo?.dateDue}
                    lastPaymentAmount={item.clearDiscountedPrice}
                    paymentDescription={`Paid ${parsePrice(
                      centsToDollars(item.alreadyPaidAmount)
                    )} of ${parsePrice(
                      centsToDollars(item.clearDiscountedPriceFull)
                    )}`}
                    lastPaymentDescription={`Last payment: ${parsePrice(
                      centsToDollars(item.clearDiscountedPrice)
                    )} on ${parseDate(
                      currentProvidersPaidInfo?.dateDue,
                      true,
                      false,
                      "MMM D, YYYY"
                    )}`}
                    isOrderPaid={isOrderPaid}
                  />
                ))}
            </StyledCardContainer>
            <InfoRow />
            <Button
              className="w-full justify-content-center border-round-lg"
              onClick={() => {
                setShowCardDetails(false);
                setShowPaymentSection({
                  paymentPlan: false,
                  paymentSection: true,
                  paymentSuccess: false,
                  receiptAvailable: false,
                });
                setShowUpdateSuccessFull(false);
                setShowReceipt(false);
              }}
              disabled={payMoreButtonDisabled || isOrderPaid}
            >
              {payMoreButtonDisabled
                ? "Last transfer is being processed..."
                : "Pay More"}
            </Button>
            <Button
              className="w-full justify-content-center border-round-lg mt-2 p-button-outlined"
              onClick={() => {
                setShowCardDetails(true);
                setShowPaymentSection({
                  paymentPlan: false,
                  paymentSection: false,
                  paymentSuccess: false,
                  receiptAvailable: false,
                });
                setShowUpdateSuccessFull(false);
                setShowReceipt(false);
              }}
              disabled={isOrderPaid}
            >
              Update Card
            </Button>
            <Questions
              deductible={benefits.remainingDeductibleInCents}
              outOfPocket={benefits.outOfPocketMaxInCents}
              coinsurance={benefits.coinsurancePercent}
            />
            <BalanceHistory
              balanceHistory={balanceHistory}
              patientName={`${orderInfo.patient.firstName} ${orderInfo.patient.lastName}`}
              addClearFee={addClearFee}
              clearValueInPercentage={Number(
                orderInfo.clearFeeValueInPercentage
              )}
            />
          </>
        )}
      </StyledPageContainer>
    </div>
  );
}
